import { useEffect, useMemo, useState } from "react";

import * as Api from "@/api";
import AddCard from "@/design/components/AddCard";
import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { viXmark } from "@sippy-platform/valkyrie";

import {
  Box,
  Card,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import AddHandsetItem from "./AddHandsetItem";
import HandsetItem from "./HandsetItem";

function Handsets({ provisioningDispatch, specs, provisioning }) {
  const [currentAccountHandset, setCurrentAccountHandset] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);

  const handsets = useMemo(() => {
    return Array.from({ length: Math.max(specs.accounts - 1, 0) }, (_, index) => {
      const handset = index + 2;
      const currentAccount = provisioning.accounts.find((account) => account.handset === handset);

      if (currentAccount) {
        return {
          handset,
          id: currentAccount.id,
          username: currentAccount.sippeer.name,
        };
      }

      return undefined;
    });
  }, [specs, provisioning.accounts]);

  useEffect(() => {
    Promise.resolve(Api.getProvisioningUsers()).then((response) => {
      setAvailableUsers(response.data?.sippeers);
    });
  }, [provisioning]);

  const handleOpenDialogAccount = (event, handset) => {
    setDrawer(true);
    setCurrentAccountHandset(handset);
  };

  // Drawer
  const [drawer, setDrawer] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(availableUsers, searchValue, ["name", "exten"]);

  const closeDrawer = () => {
    setDrawer(undefined);
    setSearchValue("");
    setCurrentAccountHandset(null);
  };

  return (
    <Grid size={12}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
          gap: 1,
        }}
      >
        {provisioning?.sippeer && (
          <Card variant="outlined">
            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar text={provisioning.sippeer.name} />
                </ListItemAvatar>
                <ListItemText primary={provisioning.sippeer.name} secondary="Handset 1" />
              </ListItem>
            </List>
          </Card>
        )}
        {handsets.map((account, key) => {
          if (account === undefined && handsets.findIndex((handset) => handset === undefined) === key) {
            return (
              <AddCard
                key={key}
                id="openDialogAccount"
                title="Add account"
                subtitle={`Handset ${key + 2}`}
                onClick={(event) => handleOpenDialogAccount(event, key + 2)}
              />
            );
          } else if (account !== undefined) {
            return (
              <HandsetItem
                key={key}
                account={account}
                provisioning={provisioning}
                provisioningDispatch={provisioningDispatch}
                setAvailableUsers={setAvailableUsers}
              />
            );
          }
        })}
      </Box>
      <Drawer
        open={!!drawer}
        onClose={closeDrawer}
        anchor="right"
        slotProps={{
          paper: {
            sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
            elevation: 4,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">Handset {currentAccountHandset}</Typography>
          <IconButton onClick={() => closeDrawer()} color="inherit">
            <Valkyrie icon={viXmark} />
          </IconButton>
        </Stack>
        <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
          <Search blur value={searchValue} onChange={setSearchValue} />
        </Box>
        <List>
          {searchResults
            .filter((user) => !handsets.find((_handset) => _handset?.username === user.name))
            .map((user) => (
              <AddHandsetItem
                key={user.id}
                user={user}
                provisioning={provisioning}
                provisioningDispatch={provisioningDispatch}
                currentAccountHandset={currentAccountHandset}
                closeDrawer={closeDrawer}
              />
            ))}
        </List>
      </Drawer>
    </Grid>
  );
}

Handsets.propTypes = {
  provisioning: PropTypes.object,
  specs: PropTypes.object,
  provisioningDispatch: PropTypes.func,
};

export default Handsets;
